import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../components/Products/catalogGridStyles.js";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import useApiData from "../services/useApiData.js";
import PutData from "../services/PutData.js";
import SnackbarEx from "../components/Helpers/SnackbarEx.jsx";
import {
    ChannelPill,
    ChannelTable,
    KpiCard,
    KpiGrid,
    ReportContent,
    ReportError,
    ReportLoading,
    ReportScroll,
    ReportSection,
    ReportSplit,
    ReportToolbar,
    formatNumber,
} from "../components/Reporting/ReportPrimitives.jsx";

const CHART_PRIMARY = "#0d9488";
const FLAG_COLOR = "#0369a1";
const GOOD_COLOR = "#f0b429";
const PLATFORM_COLORS = [
    "#0d9488",
    "#0369a1",
    "#7c3aed",
    "#db2777",
    "#ea580c",
    "#ca8a04",
    "#16a34a",
    "#0f766e",
    "#4f46e5",
    "#be123c",
];

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultRange() {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { from: dayInputValue(start), to: dayInputValue(end) };
}

function formatRangeLabel(from, to) {
    const opts = { month: "short", day: "numeric", year: "numeric" };
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
    return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
}

function money(value, digits = 2) {
    if (value == null || !Number.isFinite(Number(value))) return "—";
    return Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

export default function ReportingPurchasing() {
    const initial = defaultRange();
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [sku, setSku] = useState("");
    const [applied, setApplied] = useState({ ...initial, sku: "" });
    const [gapPlatforms, setGapPlatforms] = useState([]);
    const [savedGapPlatforms, setSavedGapPlatforms] = useState([]);
    const [savingSettings, setSavingSettings] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });

    const url = useMemo(() => {
        const params = new URLSearchParams({
            from: applied.from,
            to: applied.to,
        });
        if (applied.sku) params.set("sku", applied.sku);
        return `api/broylesllc/reports/purchasing-scorecard?${params.toString()}`;
    }, [applied]);

    const { data, error, load, refetch } = useApiData(url);
    const { data: fieldMeta } = useApiData("api/broylesllc/catalog-field-meta");
    const platformChoices = useMemo(() => {
        const fromMeta = (fieldMeta?.catalogItem?.platform?.choices || []).filter(Boolean);
        const merged = new Set([...fromMeta, ...gapPlatforms, ...savedGapPlatforms]);
        return [...merged];
    }, [fieldMeta, gapPlatforms, savedGapPlatforms]);

    useEffect(() => {
        const saved = data?.settings?.gapPlatforms;
        if (!Array.isArray(saved)) return;
        setGapPlatforms(saved);
        setSavedGapPlatforms(saved);
    }, [data?.settings?.gapPlatforms]);

    const settingsDirty = useMemo(() => {
        if (gapPlatforms.length !== savedGapPlatforms.length) return true;
        const saved = new Set(savedGapPlatforms);
        return gapPlatforms.some((name) => !saved.has(name));
    }, [gapPlatforms, savedGapPlatforms]);

    const kpis = data?.kpis;
    const volumePoints = data?.transactionsOverTime?.points || [];
    const platformSeries = data?.transactionsByPlatform?.series || [];
    const flagBreakdown = data?.flagBreakdown;

    const chartLabels = useMemo(
        () => volumePoints.map((point) => point.label || point.date),
        [volumePoints]
    );
    const chartCounts = useMemo(
        () => volumePoints.map((point) => point.count || 0),
        [volumePoints]
    );

    const platformChartSeries = useMemo(
        () =>
            platformSeries.map((series, index) => ({
                id: series.platform,
                label: series.platform,
                data: series.points.map((point) => point.count || 0),
                color: PLATFORM_COLORS[index % PLATFORM_COLORS.length],
                showMark: false,
                curve: "linear",
            })),
        [platformSeries]
    );

    const pieData = useMemo(() => {
        const good = flagBreakdown?.good || 0;
        const flag = flagBreakdown?.flag || 0;
        const unlabeled = flagBreakdown?.unlabeled || 0;
        return [
            { id: 0, value: good, label: `GOOD (${formatNumber(good, 0)})`, color: GOOD_COLOR },
            { id: 1, value: flag, label: `FLAG (${formatNumber(flag, 0)})`, color: FLAG_COLOR },
            ...(unlabeled
                ? [
                      {
                          id: 2,
                          value: unlabeled,
                          label: `Unlabeled (${formatNumber(unlabeled, 0)})`,
                          color: "#94a3b8",
                      },
                  ]
                : []),
        ].filter((entry) => entry.value > 0);
    }, [flagBreakdown]);

    const pivotRows = useMemo(
        () =>
            (data?.pivot || []).map((row) => ({
                key: row.platform,
                channel: row.platform,
                flag: row.flag,
                good: row.good,
                total: row.total,
            })),
        [data]
    );

    const applyFilters = (event) => {
        event.preventDefault();
        setApplied({ from, to, sku: sku.trim() });
    };

    const resetFilters = () => {
        const next = defaultRange();
        setFrom(next.from);
        setTo(next.to);
        setSku("");
        setApplied({ ...next, sku: "" });
    };

    const saveGapPlatforms = async () => {
        setSavingSettings(true);
        const result = await PutData(
            { settings: { gapPlatforms } },
            "broylesllc/reports/settings/purchasingScorecard"
        );
        setSavingSettings(false);
        if (!result.success) {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Could not save platform settings.",
            });
            return;
        }
        const saved = result.data?.settings?.gapPlatforms || gapPlatforms;
        setGapPlatforms(saved);
        setSavedGapPlatforms(saved);
        setSnack({ open: true, color: "success", message: "Gap platforms saved." });
        refetch();
    };

    const platformHint =
        savedGapPlatforms.length > 0
            ? `${savedGapPlatforms.length} platform${savedGapPlatforms.length === 1 ? "" : "s"} selected`
            : "Select platforms for gap cards";

    return (
        <CatalogPageShell
            title="Purchasing Scorecard"
            subtitle="Hits, misses, overages, and savings from Item Catalog purchases."
        >
            <ReportScroll>
                <form onSubmit={applyFilters}>
                    <ReportToolbar>
                        <TextField
                            size="small"
                            label="SKU Name"
                            placeholder="All SKUs"
                            value={sku}
                            onChange={(event) => setSku(event.target.value)}
                            sx={{ ...catalogSearchFieldSx, minWidth: 200, maxWidth: 280 }}
                        />
                        <PullDateRangeFields
                            layout="row"
                            startDate={from}
                            endDate={to}
                            onStartChange={setFrom}
                            onEndChange={setTo}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            size="small"
                            disabled={load}
                            startIcon={
                                load ? <CircularProgress size={14} color="inherit" /> : null
                            }
                            sx={catalogPrimaryButtonSx}
                        >
                            {load ? "Loading…" : "Apply"}
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={resetFilters}
                            sx={catalogQuietButtonSx}
                        >
                            Reset
                        </Button>
                        <div className="report-toolbar-spacer" />
                        <div className="report-toolbar-meta">
                            {formatRangeLabel(applied.from, applied.to)}
                            {applied.sku ? ` · SKU “${applied.sku}”` : ""}
                        </div>
                    </ReportToolbar>
                </form>

                <div className="report-toolbar" style={{ top: "auto", position: "relative" }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={platformChoices}
                        value={gapPlatforms}
                        onChange={(_event, next) => setGapPlatforms(next)}
                        disableCloseOnSelect
                        ListboxProps={{ style: { maxHeight: 220 } }}
                        renderTags={(selected) =>
                            selected.length ? (
                                <Tooltip title={selected.join(", ")} arrow>
                                    <Chip
                                        size="small"
                                        label={
                                            selected.length === 1
                                                ? selected[0]
                                                : `${selected.length} platforms`
                                        }
                                        onDelete={() => setGapPlatforms([])}
                                        sx={{
                                            maxWidth: 180,
                                            height: 24,
                                            "& .MuiChip-label": { px: 1 },
                                        }}
                                    />
                                </Tooltip>
                            ) : null
                        }
                        sx={{
                            width: 280,
                            flex: "0 0 auto",
                            "& .MuiAutocomplete-inputRoot": {
                                flexWrap: "nowrap",
                                overflow: "hidden",
                            },
                            "& .MuiAutocomplete-input": {
                                minWidth: "48px !important",
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Gap platforms"
                                placeholder={gapPlatforms.length ? "Add…" : "Select platforms"}
                                sx={catalogSearchFieldSx}
                            />
                        )}
                    />
                    <Button
                        type="button"
                        variant="contained"
                        size="small"
                        disabled={!settingsDirty || savingSettings}
                        onClick={saveGapPlatforms}
                        startIcon={
                            savingSettings ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save platforms
                    </Button>
                    {settingsDirty ? (
                        <div className="report-toolbar-meta">Unsaved changes</div>
                    ) : null}
                </div>

                {error ? <ReportError message="Failed to load purchasing scorecard." /> : null}

                {load && !data ? (
                    <ReportLoading label="Crunching purchasing scorecard…" />
                ) : data ? (
                    <ReportContent>
                        <KpiGrid columns={3}>
                            <KpiCard
                                label="Count: Total"
                                value={formatNumber(kpis?.totalCount, 0)}
                                hint="Daily Minimum - 60+"
                                tone="accent"
                            />
                            <KpiCard
                                label="Count: Gap Platforms"
                                value={formatNumber(kpis?.vistaMacCount, 0)}
                                hint={platformHint}
                                tone="accent"
                            />
                            <KpiCard
                                label="Overbid Items"
                                value={formatNumber(kpis?.overbidCount, 0)}
                                hint="Also includes items not linked to SKU"
                                tone="negative"
                            />
                            <KpiCard
                                label="Purchasing Labor $ per Item"
                                value={money(kpis?.purchasingLaborPerItem)}
                                hint="Pro Forma - $2"
                            />
                            <KpiCard
                                label="Purchasing Gap"
                                value={money(kpis?.purchasingGapVistaMac)}
                                hint="Sum on selected platforms"
                            />
                            <KpiCard
                                label="Average Purchasing Gap"
                                value={money(kpis?.averagePurchasingGapVistaMac)}
                                hint="Average on selected platforms"
                            />
                        </KpiGrid>

                        <ReportSplit>
                            <ReportSection
                                eyebrow="Volume"
                                title="Transaction Count"
                                subtitle={
                                    data.transactionsOverTime?.granularity === "week"
                                        ? "Weekly purchases in range"
                                        : "Daily purchases in range"
                                }
                            >
                                <div className="report-panel">
                                    <div className="report-panel-body">
                                        {volumePoints.length ? (
                                            <div className="report-chart">
                                                <LineChart
                                                    height={280}
                                                    series={[
                                                        {
                                                            data: chartCounts,
                                                            label: "Transactions",
                                                            color: CHART_PRIMARY,
                                                            showMark: false,
                                                            curve: "linear",
                                                        },
                                                    ]}
                                                    xAxis={[{ data: chartLabels, scaleType: "point" }]}
                                                    margin={{ left: 50, right: 16, top: 20, bottom: 40 }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="report-chart-empty">No purchases in this range.</p>
                                        )}
                                    </div>
                                </div>
                            </ReportSection>

                            <ReportSection
                                eyebrow="Flags"
                                title="Overbid Orders"
                                subtitle="Purchasing Flag on catalog items"
                            >
                                <div className="report-panel">
                                    <div className="report-panel-body">
                                        {pieData.length ? (
                                            <div className="report-chart">
                                                <PieChart
                                                    height={280}
                                                    series={[
                                                        {
                                                            data: pieData,
                                                            innerRadius: 48,
                                                            outerRadius: 100,
                                                            paddingAngle: 2,
                                                            cornerRadius: 4,
                                                        },
                                                    ]}
                                                    margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="report-chart-empty">No flagged purchases yet.</p>
                                        )}
                                    </div>
                                </div>
                            </ReportSection>
                        </ReportSplit>

                        <ReportSection
                            eyebrow="Platforms"
                            title="Transactions by Platform"
                            subtitle="Top platforms over the selected purchase dates"
                        >
                            <div className="report-panel">
                                <div className="report-panel-body">
                                    {platformChartSeries.length && chartLabels.length ? (
                                        <div className="report-chart">
                                            <LineChart
                                                height={320}
                                                series={platformChartSeries}
                                                xAxis={[{ data: chartLabels, scaleType: "point" }]}
                                                margin={{ left: 50, right: 16, top: 20, bottom: 40 }}
                                                slotProps={{ legend: { hidden: false } }}
                                            />
                                        </div>
                                    ) : (
                                        <p className="report-chart-empty">No platform volume in this range.</p>
                                    )}
                                </div>
                            </div>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Pivot"
                            title="Hits and misses by platform"
                            subtitle="FLAG, GOOD, and totals from Purchasing Flag"
                        >
                            <div className="report-panel">
                                <div className="report-panel-body">
                                    <ChannelTable
                                        rows={pivotRows}
                                        emptyLabel="No catalog purchases match these filters."
                                        columns={[
                                            {
                                                key: "channel",
                                                label: "Platform",
                                                render: (row) => <ChannelPill channel={row.channel} />,
                                            },
                                            {
                                                key: "flag",
                                                label: "FLAG",
                                                align: "right",
                                                render: (row) => formatNumber(row.flag, 0),
                                            },
                                            {
                                                key: "good",
                                                label: "GOOD",
                                                align: "right",
                                                render: (row) => formatNumber(row.good, 0),
                                            },
                                            {
                                                key: "total",
                                                label: "Total",
                                                align: "right",
                                                primary: true,
                                                render: (row) => formatNumber(row.total, 0),
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </ReportSection>
                    </ReportContent>
                ) : null}
            </ReportScroll>
            <SnackbarEx
                open={snack.open}
                color={snack.color}
                message={snack.message}
                close={() => setSnack((prev) => ({ ...prev, open: false }))}
            />
        </CatalogPageShell>
    );
}
